<template>
  <div class="news-composer">
    <!--
      NO TWO-WAY BINDING ANYWHERE IN THIS FILE, and that is a contract rather
      than a style preference. This component owns no copy of the fields: it
      renders the props it is given and emits the whole draft back on every edit,
      so the page above it holds one source of truth and can compare it against a
      baseline. A two-way binding here would give the composer a second, drifting
      copy, and the dirty comparison would then be against a value the page never
      received. FeedbackSheetModal.vue:33-41 binds the editor the same way.
    -->
    <f7-list class="list-strong list-outline list-dividers">
      <f7-list-input
        label="Kop"
        type="text"
        placeholder="Nieuwsbrief augustus"
        maxlength="191"
        :value="title"
        :info="title.length + '/191'"
        @input="emitField('title', $event.target.value)"
      ></f7-list-input>

      <f7-list-input
        label="Ondertitel"
        type="text"
        placeholder="Waar gaat dit bericht over?"
        maxlength="191"
        :value="subtitle"
        :info="subtitle.length + '/191'"
        @input="emitField('subtitle', $event.target.value)"
      ></f7-list-input>
    </f7-list>

    <f7-block-title>Bericht</f7-block-title>

    <!--
      `:value` + `@texteditor:change`, exactly as FeedbackSheetModal.vue:33-41
      binds it. The round trip is safe because setValue() returns early when the
      new value equals the current one (text-editor-class.js:128), so the caret
      does not move while the author types.
    -->
    <f7-text-editor
      class="news-editor"
      mode="toolbar"
      resizable
      placeholder="Schrijf je bericht…"
      :value="content"
      :buttons="editorButtons"
      @texteditor:change="emitField('content', $event)"
    ></f7-text-editor>

    <f7-block-footer class="news-composer-counter">
      {{ content.length }}/60000
    </f7-block-footer>

    <f7-list class="list-strong list-outline list-dividers">
      <!--
        Native `type="date"`, following UserSubscriptionDetails.vue:71-101. The
        OS supplies the picker and the localisation on iOS, Android and desktop
        alike, the value format is `YYYY-MM-DD`, which is what the API's
        `date:Y-m-d` cast expects, and it costs no new dependency. This is the
        concrete answer to CONTEXT.md's "date pickers that work".
      -->
      <f7-list-input
        label="Zichtbaar vanaf"
        type="date"
        placeholder=""
        :value="visible_from"
        @input="emitField('visible_from', $event.target.value)"
      ></f7-list-input>

      <!--
        "tot en met", never "tot". SC4 requires visibility ON the end date, and
        the API bug this phase fixed — strict comparisons making an item vanish a
        day early and never appear on its own start date — is the machine version
        of exactly the ambiguity this wording removes. The copy and the
        comparison operators have to agree, and both are inclusive.
      -->
      <f7-list-input
        label="Zichtbaar tot en met"
        type="date"
        placeholder=""
        :value="visible_to"
        @input="emitField('visible_to', $event.target.value)"
      ></f7-list-input>
    </f7-list>

    <!--
      ALWAYS RENDERED, and never blank. Eight mutually exclusive variants
      produced by ONE computed rather than eight template branches: eight
      branches is eight places for a ninth state to fall through and render
      nothing at all.

      This is not an error surface. An end date before a start date is reported
      here as a STATE, in the same line and the same Label styling as every other
      state, because it is a thing the operator is currently doing rather than a
      failure. The server's `after_or_equal` rule is the authority; this line is
      what stops the author reaching it.
    -->
    <f7-block-footer class="news-composer-visibility">
      {{ visibilitySummary }}
    </f7-block-footer>

    <!--
      A5. PERSISTENT, NOT A TOAST — persistence is the signal of importance here,
      not hue. No warning colour is introduced: the save SUCCEEDED, the content
      is stored, and nothing is broken. Painting this amber would spend a colour
      on a successful outcome.
    -->
    <f7-block v-if="showRemovalNotice" strong inset class="news-composer-notice">
      <p class="news-composer-notice-heading">
        <f7-icon
          f7="exclamationmark_triangle"
          class="news-composer-notice-icon"
        ></f7-icon>
        Een deel van je opmaak is verwijderd
      </p>

      <!--
        TWO BODIES, and the condition below is the whole reason the second one
        exists. A cleaner that normalises whitespace or attribute order reports
        `changed: true` with three empty lists, and rendering the first body then
        produces `opgeslagen zonder .` — a sentence with a hole in it.
      -->
      <!-- Both bodies are kept on ONE source line each, unwrapped, so the
           authored Dutch can be matched against this file as a whole string. -->
      <p v-if="removedList !== ''" class="news-composer-notice-body">Dit bericht is opgeslagen zonder {{ removedList }}. Opmaak die onveilig kan zijn in de app wordt automatisch weggehaald.</p>
      <p v-else class="news-composer-notice-body">Je opmaak is licht aangepast zodat die veilig in de app werkt. Er is geen tekst verwijderd.</p>

      <f7-block-footer class="news-composer-notice-footnote">
        De tekst hierboven is wat leden te zien krijgen.
      </f7-block-footer>
    </f7-block>
  </div>
</template>

<script>
import moment from "moment/dist/moment";
import "moment/dist/locale/nl";
moment.locale("nl");

/**
 * FOUR GROUPS, WHERE FRAMEWORK7'S DEFAULT IS SEVEN.
 *
 * The button set is a security decision as much as an editorial one: every
 * control dropped is a tag or an attribute the cleaner's allowlist does not have
 * to permit, and every removal below is backed by a measurement against that
 * allowlist rather than by a preference.
 *
 *  - The picture control is gone. CONTEXT.md defers uploads and says the feature
 *    should be disabled rather than half-built; Framework7's own control prompts
 *    for a URL and inserts an `<img src>` the allowlist deletes, which is exactly
 *    the half-built state it warns about.
 *  - The four alignment controls are gone. Each emits an inline
 *    `style="text-align: …"`, so keeping them would force the allowlist to
 *    permit a `style` attribute at all. It must not: `style` is how a
 *    fixed-position, full-viewport overlay comes out of "just formatting".
 *  - The heading-one control is gone. The item's own `title` field IS the
 *    article's top-level heading, rendered at 27px on the member's card, so a
 *    second one inside the body is a hierarchy error and a semantics error at
 *    once. `paragraph` stays, so the author can get back out of a heading.
 *  - The six remaining defaults are gone — two line decorations, the two
 *    typographic shifts used for footnotes, and the pair that nudges a block
 *    sideways. None is used by the newsletter this replaces, each is allowlist
 *    surface for no editorial gain, and non-link text with a line under it is
 *    its own usability defect.
 *  - `link` STAYS. The cleaner restricts `href` to http, https and mailto on
 *    both sides, which is what makes a `javascript:` link render inert rather
 *    than being an argument for removing links altogether.
 *
 * Frozen so a later edit to one composer instance cannot mutate the array every
 * instance shares.
 */
const EDITOR_BUTTONS = Object.freeze([
  Object.freeze(["bold", "italic"]),
  Object.freeze(["orderedList", "unorderedList"]),
  Object.freeze(["link"]),
  Object.freeze(["paragraph", "h2", "h3"]),
]);

/** The formats the API is contracted to emit for a `date` column. */
const DATE_FORMATS = ["YYYY-MM-DD", moment.ISO_8601];

/**
 * Strict parse of a date field, or null.
 *
 * Returning null rather than an invalid moment is what keeps `Invalid date` out
 * of the visibility line: every branch below checks for null first and falls
 * back to authored Dutch. A legacy row with a null date, and a half-typed value
 * from a browser that reports its date input mid-edit, both land here.
 *
 * @param {?string} value
 * @returns {?Object} a valid moment, or null
 */
function parseDate(value) {
  if (!value) {
    return null;
  }

  const parsed = moment(value, DATE_FORMATS, true);

  return parsed.isValid() ? parsed : null;
}

export default {
  components: {},

  /**
   * DECLARED, and load-bearing rather than documentation.
   *
   * `change` is also a NATIVE DOM event name, and this component's root contains
   * four `<input>` elements that each fire one. Without this declaration Vue
   * treats a parent's `@change` as a fallthrough attribute and attaches it to the
   * root element as a native listener — so the page's handler would fire a second
   * time, with a raw `Event` instead of the draft object, every time a date field
   * blurs. That handler assigns the draft, so the draft would be replaced by an
   * Event and the whole form would empty. Measured in this plan's own test run.
   */
  emits: ["change"],

  props: {
    /**
     * The five field values. This component holds NO copy of them — see the
     * comment at the top of the template.
     */
    title: { default: "" },
    subtitle: { default: "" },
    content: { default: "" },
    visible_from: { default: "" },
    visible_to: { default: "" },

    /**
     * The server's removal report from the last successful save, or null.
     * Shape: `{changed, removed_tags, removed_attributes, removed_protocols}`.
     */
    sanitisation: { default: null },

    /**
     * True while a save is in flight. Used for one thing only: keeping the
     * notice off screen until there is a definite answer, so it never appears
     * speculatively over a save that has not landed.
     */
    saving: { default: false },
  },

  mixins: [],

  data: function () {
    return {
      //
    };
  },

  computed: {
    /**
     * @returns {Array} the four button groups, see EDITOR_BUTTONS
     */
    editorButtons() {
      return EDITOR_BUTTONS;
    },

    /**
     * Exactly one of eight mutually exclusive states, as authored Dutch.
     *
     * Order matters. Both-null and single-null come first so a missing date can
     * never reach a comparison; the inverted range comes before the
     * today-relative branches, because an inverted window matches nothing on any
     * day and calling it "not yet visible" would promise a start date that never
     * arrives; and the equal-dates case comes before the three window branches
     * because "only on this day" is more specific than any of them.
     *
     * @returns {string} never empty, never `Invalid date`
     */
    visibilitySummary() {
      const from = parseDate(this.visible_from);
      const to = parseDate(this.visible_to);

      if (from === null && to === null) {
        return "Kies een begindatum en een einddatum. Zonder periode is dit bericht voor niemand zichtbaar.";
      }

      if (to === null) {
        return "Kies ook een einddatum.";
      }

      if (from === null) {
        return "Kies ook een begindatum.";
      }

      if (to.isBefore(from, "day")) {
        return "De einddatum ligt vóór de begindatum. Leden zien dit bericht dan nooit.";
      }

      if (to.isSame(from, "day")) {
        return "Alleen zichtbaar op " + from.format("D MMMM YYYY") + ".";
      }

      const today = moment();

      if (today.isBefore(from, "day")) {
        return (
          "Nog niet zichtbaar. Leden zien dit vanaf " +
          from.format("D MMMM YYYY") +
          "."
        );
      }

      if (today.isAfter(to, "day")) {
        return (
          "Niet meer zichtbaar. De periode liep tot en met " +
          to.format("D MMMM YYYY") +
          "."
        );
      }

      // Both boundary days are INSIDE the window, matching the server's now
      // inclusive comparison. The two `isBefore`/`isAfter` checks above are
      // strict on purpose, so today == from and today == to both land here.
      return (
        "Nu zichtbaar voor leden, tot en met " + to.format("D MMMM YYYY") + "."
      );
    },

    /**
     * Everything the server reported removing, in the notation the author sees:
     * a tag as `<script>`, an attribute as `onerror`, a blocked scheme as
     * `javascript:-links`.
     *
     * Every key is guarded rather than assumed present, so a report that carries
     * only `changed` still renders the normalisation-only body instead of
     * throwing on `undefined.map`.
     *
     * @returns {string[]}
     */
    removedItems() {
      const report = this.sanitisation || {};

      return [
        ...(report.removed_tags || []).map((tag) => "<" + tag + ">"),
        ...(report.removed_attributes || []),
        ...(report.removed_protocols || []).map((scheme) => scheme + ":-links"),
      ];
    },

    /**
     * The removed items as a Dutch enumeration: `, ` between items and ` en `
     * before the last. Empty string when nothing was named, which is what the
     * template's `v-if` keys off.
     *
     * @returns {string}
     */
    removedList() {
      const items = this.removedItems;

      if (items.length === 0) {
        return "";
      }

      if (items.length === 1) {
        return items[0];
      }

      return items.slice(0, -1).join(", ") + " en " + items[items.length - 1];
    },

    /**
     * The notice exists only once there is a definite answer.
     *
     * Absent when the server reported nothing changed — a save that changed
     * nothing needs no report, and a "niets verwijderd" reassurance block would
     * be noise on every ordinary save. Absent while a save is in flight, so the
     * previous save's report cannot be read as this one's.
     *
     * @returns {boolean}
     */
    showRemovalNotice() {
      return (
        !this.saving &&
        this.sanitisation !== null &&
        typeof this.sanitisation === "object" &&
        this.sanitisation.changed === true
      );
    },
  },

  created() {
    //
  },

  mounted() {
    // TWO MEASURED SILENT-LOSS GUARDS, not defensive noise.
    //
    // 1. Chromium's default block separator is `<div>`, so pressing Enter emits
    //    `<div>regel</div>` rather than a paragraph. `div` IS allowlisted, so
    //    this is belt and braces rather than the only defence — but a paragraph
    //    is what `.news-content` styles a rhythm for.
    // 2. In CSS mode WebKit's bold emits `<span style="font-weight: bold">`,
    //    which reduces to bare text on save: the emphasis vanishes with no
    //    error, and the author is left with "bold worked while I typed and is
    //    gone now". Turning CSS mode off makes it emit `<b>` instead, which is
    //    allowlisted.
    //
    // Wrapped because `document.execCommand` is deprecated and may be absent or
    // throwing in some environments; neither call is load-bearing enough to stop
    // the composer mounting.
    try {
      document.execCommand("defaultParagraphSeparator", false, "p");
      document.execCommand("styleWithCSS", false, false);
    } catch (error) {
      // Deliberately swallowed. Both calls are hardening; a document that
      // refuses them still renders a usable composer.
    }
  },

  methods: {
    /**
     * Emits the WHOLE draft with one field replaced.
     *
     * The page above owns the single source of truth and the dirty comparison,
     * so it must receive a complete object rather than a field name and a value
     * it then has to merge.
     *
     * @param {string} field one of the five field names
     * @param {string} value
     */
    emitField(field, value) {
      this.$emit("change", {
        title: this.title,
        subtitle: this.subtitle,
        content: this.content,
        visible_from: this.visible_from,
        visible_to: this.visible_to,
        [field]: value,
      });
    },
  },

  watch: {
    //
  },
};
</script>

<style scoped>
/*
  `:deep()` is required rather than decorative. `.text-editor-content` is
  rendered by framework7-vue's own component, and a Vue scoped rule only tags the
  elements this template writes plus the child's ROOT element — so a plain
  `.news-editor .text-editor-content` would compile to
  `.news-editor .text-editor-content[data-v-…]` and match nothing at all. The
  `:deep()` form compiles to `.news-editor[data-v-…] .text-editor-content`, and
  `.news-editor` IS the child's root, which does carry the scope id.

  NOTE (carried to 43-10): vitest does not process an SFC <style> block at all,
  so everything below is asserted as SOURCE TEXT and no test observes a computed
  declaration. 43-08 logged the same limit for its width cap.
*/

/*
  The editor is `resizable`, and text-editor.less:15 turns that into
  `height: auto`. Without a floor an EMPTY editor collapses to a toolbar strip
  with one line under it, which is a terrible invitation to write a newsletter
  in. 240px is roughly sixteen lines at 15px/1.5 and grows from there, so no
  inner scrollbar fights the page scroll.
*/
.news-composer .news-editor :deep(.text-editor-content) {
  min-height: 240px;
}

/*
  Sticky rather than fixed: the toolbar stays reachable while writing a long
  article, and scrolls away with the editor once the author has moved past it.
*/
.news-composer .news-editor :deep(.text-editor-toolbar) {
  position: sticky;
  top: 0;
  z-index: 1;
}

.news-composer-counter {
  text-align: right;
}

/*
  The Heading role, taken entirely from Framework7's own tokens. NO NUMERIC FONT
  SIZE IS AUTHORED ANYWHERE IN THIS FILE — a fifth size on one screen is how a
  design system stops being one.
*/
.news-composer-notice-heading {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
  margin-top: 0;
  margin-bottom: 8px;
}

/*
  A neutral, not a fifth colour: the same secondary text colour A2's row text,
  the visibility line and every footnote in this phase already use.
*/
.news-composer-notice-icon {
  color: var(--f7-list-item-text-text-color);
  margin-right: 4px;
  vertical-align: middle;
}

.news-composer-notice-body {
  margin-top: 0;
  margin-bottom: 16px;
}

.news-composer-notice-footnote {
  margin-bottom: 0;
}
</style>
